import { BreadcrumbComponent } from '@bookinglab/ui';
import {
	HttpClient,
	HTTP_INTERCEPTORS,
	provideHttpClient,
	withInterceptorsFromDi,
} from '@angular/common/http';
import { APP_INITIALIZER, NgModule, LOCALE_ID } from '@angular/core';

import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AppConfig } from './app.config';
import { BreadcrumbsComponent } from './breadcrumbs/breadcrumbs.component';
import { FooterComponent } from './footer/footer/footer.component';
import { HeaderComponent } from './header/header/header.component';
import { DepartmentsComponent } from './journey/departments/departments.component';
import { LocationComponent } from './journey/location/location.component';
import { LoginComponent } from './journey/login/login.component';
import { ServicesComponent } from './journey/services/services.component';
import { AlertModule } from './_alert';
import { CalendarComponent } from './journey/calendar/calendar.component';
import { BookingDetailsComponent } from './journey/booking-details/booking-details.component';
import { SummaryComponent } from './journey/summary/summary.component';
import { ConfirmationComponent } from './journey/confirmation/confirmation.component';
import { LeafletMapComponent } from './leaflet-map/leaflet-map.component';
import {
	MatDatepickerIntl,
	MatDatepickerModule,
} from '@angular/material/datepicker';
import {
	MatNativeDateModule,
	DateAdapter,
	MAT_DATE_FORMATS,
} from '@angular/material/core';
import {
	CUSTOM_DATE_FORMATS,
	CustomDateAdapter,
} from './journey/calendar/custom-date-adapter';
import { BookingSummaryComponent } from './booking-summary/booking-summary.component';
import { SkipToContentComponent } from './skip-to-content/skip-to-content.component';
import { SessionComponent } from './journey/session/session.component';
import { VerifyPaymentComponent } from './journey/verify-payment/verify-payment.component';
import { EstoreFailComponent } from './journey/estore-fail/estore-fail.component';

// Material
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSliderModule } from '@angular/material/slider';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatMenuModule } from '@angular/material/menu';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatListModule } from '@angular/material/list';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatCardModule } from '@angular/material/card';
import { MatStepperModule } from '@angular/material/stepper';
import { MatTabsModule } from '@angular/material/tabs';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatChipsModule } from '@angular/material/chips';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatDialogModule } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTableModule } from '@angular/material/table';
import { MatSortModule } from '@angular/material/sort';
import { MatPaginatorModule } from '@angular/material/paginator';
import { SanitiseHtmlPipe } from './pipes/sanitise-html.pipe';
import { TimeoutComponent } from '@bookinglab/ui';

import { NgxSpinnerModule } from 'ngx-spinner';
import { MemberBarComponent } from './member-bar/member-bar.component';
import { ModalComponent } from './modal/modal.component';
import { MatCalendarComponent } from './journey/mat-calendar/mat-calendar.component';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { HttpInterceptorInterceptor } from './services/http-interceptor.interceptor';
import { Globals } from './globals';
import { TranslateSplitPipe } from './pipes/translate-split.pipe';
import { LineSplitPipe } from './pipes/line-split.pipe';
import { NgxMaskModule, IConfig } from 'ngx-mask';
import { registerLocaleData } from '@angular/common';
import localeEn from '@angular/common/locales/en';
registerLocaleData(localeEn);

// Required for MSAL
import { IPublicClientApplication, PublicClientApplication, InteractionType, BrowserCacheLocation, LogLevel } from '@azure/msal-browser';
import { MsalGuard, MsalInterceptor, MsalBroadcastService, MsalInterceptorConfiguration, MsalModule, MsalService, MSAL_GUARD_CONFIG, MSAL_INSTANCE, MSAL_INTERCEPTOR_CONFIG, MsalGuardConfiguration, MsalRedirectComponent } from '@azure/msal-angular';
const isIE = window.navigator.userAgent.indexOf('MSIE ') > -1 || window.navigator.userAgent.indexOf('Trident/') > -1;

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      // 'Application (client) ID' of app registration in the Microsoft Entra admin center - this value is a GUID
      clientId: "74550c6b-c499-4ce6-9394-12d7a45ed227",
      // Full directory URL, in the form of https://login.microsoftonline.com/<tenant>
      authority: "https://login.microsoftonline.com/50d8c115-b77f-4395-a3ba-3b407caf0d88",
      // Must be the same redirectUri as what was provided in your app registration.
      redirectUri: "https://westminster.ceremonies.registrars.staging.bookinglab.co.uk",
    },
    // auth: {
    //   // 'Application (client) ID' of app registration in the Microsoft Entra admin center - this value is a GUID
    //   clientId: "5b3f19c3-e0a2-4704-9c73-844021e9383d",
    //   // Full directory URL, in the form of https://login.microsoftonline.com/<tenant>
    //   authority: "https://login.microsoftonline.com/99790039-f035-4127-9efe-6e8c7057d4ab",
    //   // Must be the same redirectUri as what was provided in your app registration.
    //   redirectUri: "https://localhost:4201"
    // },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      storeAuthStateInCookie: isIE
    }
  });
}

// MSAL Interceptor is required to request access tokens in order to access the protected resource (Graph)
export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set('https://graph.microsoft.com/v1.0/me', ['user.read']);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap
  };
}

// MSAL Guard is required to protect routes and require authentication before accessing protected routes
export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return { 
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: ['user.read']
    }
  };
}


export const options: Partial<IConfig> | (() => Partial<IConfig>) = null;

// Fonts
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { NgSelectModule } from '@ng-select/ng-select';
import { CostBreakdownComponent } from './cost-breakdown/cost-breakdown.component';
import { FormErrorMessagesComponent } from './form-error-messages/form-error-messages.component';
import { ThankyouComponent } from './journey/thankyou/thankyou.component';
import {
	BackLinkComponent,
	PageHeadingComponent,
} from '@bookinglab/govbook-ui/layout';
import { FeesAndMakeItYoursEnhancementsComponent } from './fees-and-make-it-yours-enhancements/fees-and-make-it-yours-enhancements.component';

export function initializeApp(appConfig: AppConfig) {
	return () => appConfig.load();
}

// required for AOT compilation
export function HttpLoaderFactory(httpClient: HttpClient) {
	return new TranslateHttpLoader(httpClient, './assets/i18n/', '.json');
}

@NgModule({
	declarations: [
		AppComponent,
		LocationComponent,
		DepartmentsComponent,
		FooterComponent,
		LoginComponent,
		BreadcrumbsComponent,
		SkipToContentComponent,
		CalendarComponent,
		SummaryComponent,
		ConfirmationComponent,
		LeafletMapComponent,
		MemberBarComponent,
		SanitiseHtmlPipe,
		ModalComponent,
		MatCalendarComponent,
		LineSplitPipe,
		SessionComponent,
		VerifyPaymentComponent,
		EstoreFailComponent,
		CostBreakdownComponent,
		FormErrorMessagesComponent,
		ThankyouComponent,
	],
	bootstrap: [AppComponent],
	imports: [
		BrowserModule,
		AppRoutingModule,
		TranslateModule.forRoot({
			loader: {
				provide: TranslateLoader,
				useFactory: HttpLoaderFactory,
				deps: [HttpClient],
			},
		}),
		HeaderComponent,
		BackLinkComponent,
		PageHeadingComponent,
		BreadcrumbComponent,
		TranslateSplitPipe,
		FeesAndMakeItYoursEnhancementsComponent,
		AlertModule,
		MatIconModule,
		FormsModule,
		ReactiveFormsModule,
		MatDatepickerModule,
		MatNativeDateModule,
		MatCheckboxModule,
		MatButtonModule,
		MatInputModule,
		MatAutocompleteModule,
		MatFormFieldModule,
		MatRadioModule,
		MatSelectModule,
		MatSliderModule,
		MatSlideToggleModule,
		MatMenuModule,
		MatSidenavModule,
		MatToolbarModule,
		MatListModule,
		MatGridListModule,
		MatCardModule,
		MatStepperModule,
		MatTabsModule,
		MatExpansionModule,
		MatButtonToggleModule,
		MatChipsModule,
		MatProgressSpinnerModule,
		MatProgressBarModule,
		MatDialogModule,
		MatTooltipModule,
		MatSnackBarModule,
		MatTableModule,
		MatSortModule,
		MatPaginatorModule,
		TimeoutComponent,
		BrowserAnimationsModule,
		NgxSpinnerModule,
		FontAwesomeModule,
		NgSelectModule,
		NgxMaskModule.forRoot(options),
    MsalModule
	],
	providers: [
		AppConfig,
		{
			provide: APP_INITIALIZER,
			useFactory: initializeApp,
			deps: [AppConfig],
			multi: true,
		},
		{
			provide: DateAdapter,
			useClass: CustomDateAdapter,
		},
		{ provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS },
		{
			provide: MAT_DATE_LOCALE,
			useValue: 'en-GB',
		},
		{
			provide: HTTP_INTERCEPTORS,
			useClass: HttpInterceptorInterceptor,
			multi: true,
		},
     {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
		Globals,
		provideHttpClient(withInterceptorsFromDi()),
	],
})
export class AppModule {}
